import { useState, type FormEvent } from 'react';
import { authApi, AuthApiError, type AuthEnvelope } from './authApi';
import { useT } from '@/i18n/useI18n';

interface Props {
  onClose: () => void;
  onSuccess: (env: AuthEnvelope) => void;
}

export function EmailAuthModal({ onClose, onSuccess }: Props) {
  const t = useT();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [identifier, setIdentifier] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const env =
        mode === 'login'
          ? await authApi.login({ identifier: identifier.trim(), password })
          : await authApi.register({
              email: email.trim().toLowerCase(),
              username: username.trim(),
              password,
            });
      onSuccess(env);
    } catch (err) {
      if (err instanceof AuthApiError) setError(err.message || err.code);
      else setError(t('common.network_error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        background: 'rgba(5,3,20,0.75)',
        backdropFilter: 'blur(8px)',
        zIndex: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="card"
        style={{ width: '100%', maxWidth: 360, padding: 22, position: 'relative' }}
      >
        <button
          onClick={onClose}
          aria-label={t('common.close')}
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            background: 'transparent',
            border: 'none',
            color: 'var(--ink-muted)',
            fontSize: 18,
            cursor: 'pointer',
          }}
        >
          ✕
        </button>

        <div className="display" style={{ fontSize: 22, marginBottom: 4 }}>
          {mode === 'login' ? t('login.signin') : t('login.signup')}
        </div>
        <div style={{ fontSize: 11, color: 'var(--ink-muted)', marginBottom: 18 }}>
          {mode === 'login' ? t('auth.welcome_back') : t('auth.pick_name')}
        </div>

        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {mode === 'login' ? (
            <Field label={t('login.email_or_username')}>
              <input
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                autoFocus
                autoComplete="username"
                required
                minLength={3}
                style={inputStyle}
              />
            </Field>
          ) : (
            <>
              <Field label={t('login.email')}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                  autoComplete="email"
                  required
                  style={inputStyle}
                />
              </Field>
              <Field label={t('login.username')}>
                <input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  required
                  minLength={3}
                  maxLength={20}
                  pattern="[a-zA-Z0-9_.-]+"
                  style={inputStyle}
                />
              </Field>
            </>
          )}
          <Field label={t('login.password')}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              required
              minLength={mode === 'register' ? 8 : 1}
              style={inputStyle}
            />
          </Field>

          {error && (
            <div
              style={{
                background: 'rgba(230,57,70,0.18)',
                border: '1px solid rgba(230,57,70,0.4)',
                color: 'var(--c-red)',
                padding: '8px 10px',
                borderRadius: 8,
                fontSize: 12,
              }}
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="btn btn-primary"
            style={{ fontSize: 16, padding: '12px 18px', marginTop: 6 }}
          >
            {busy ? '…' : mode === 'login' ? t('login.signin') : t('login.signup')}
          </button>

          <button
            type="button"
            onClick={() => {
              setError(null);
              setMode((m) => (m === 'login' ? 'register' : 'login'));
            }}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--c-cyan)',
              fontSize: 12,
              cursor: 'pointer',
              marginTop: 4,
            }}
          >
            {mode === 'login'
              ? `${t('login.switch_to_signup')} →`
              : `${t('login.switch_to_signin')} →`}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <span
        className="tag"
        style={{ color: 'var(--ink-dim)', fontSize: 9, letterSpacing: '0.14em' }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  background: 'rgba(0,0,0,0.4)',
  border: '1px solid var(--hairline)',
  borderRadius: 10,
  padding: '10px 12px',
  color: 'var(--ink)',
  fontFamily: 'var(--f-body)',
  fontSize: 14,
  outline: 'none',
  width: '100%',
};
